import React, { useState, FC } from "react";
import api from "../services/api";
import { useData } from "../context/DataContext";
import ExcelJS from "exceljs";

interface QrCodeAvailabilityDto {
    from: string;
    to: string;
}

interface ImportResult {
    originalId: number | string;
    newId: number | string;
    name: string;
    idChanged: boolean;
}

const BulkImportPage: FC = () => {
    const { categories, clients } = useData();

    // --- Stati del Form (Uguali a CreateQrPage) ---
    const [activeCategory, setActiveCategory] = useState<number>(-1);
    const [activeClient, setActiveClient] = useState<number>(-1);
    const [note, setNote] = useState<string>('');
    const [validFrom, setValidFrom] = useState<string>('');
    const [validTo, setValidTo] = useState<string>('');
    const [maxUses, setMaxUses] = useState<number>(-1);
    const [maxUsesFasce, setMaxUsesFasce] = useState<number>(-1);
    const [isOneShot, setIsOneShot] = useState<boolean>(false);
    const [oneShotName, setOneShotName] = useState<string>('');
    const [availabilities, setAvailabilities] = useState<QrCodeAvailabilityDto[]>([]);

    // --- Stati di Importazione ---
    const [file, setFile] = useState<File | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [importResults, setImportResults] = useState<ImportResult[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    // --- Handlers Fasce Orarie ---
    const handleAddAvailability = () => {
        setAvailabilities([...availabilities, { from: "", to: "" }]);
    };
    const handleAvailabilityChange = (index: number, field: "from" | "to", value: string) => {
        const newAvailabilities = [...availabilities];
        newAvailabilities[index][field] = value;
        setAvailabilities(newAvailabilities);
    };
    const handleRemoveAvailability = (index: number) => {
        setAvailabilities(availabilities.filter((_, i) => i !== index));
    };

    // --- Handler Importazione ---
    const handleImport = async () => {
        if (!file || activeCategory === -1) {
            setError("Seleziona un file Excel e una categoria (obbligatori).");
            return;
        }
        if (isOneShot && !oneShotName.trim()) {
            setError("Inserisci un nome per il gruppo One-Shot.");
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            const workbook = new ExcelJS.Workbook();
            const arrayBuffer = await file.arrayBuffer();
            await workbook.xlsx.load(arrayBuffer);

            const worksheet = workbook.worksheets[0];
            const qrcodesToImport: { id: any, name: string }[] = [];

            // Assume riga 1 = intestazione. Inizia da riga 2. Col A = ID, Col B = Nome
            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber > 1) {
                    const rawId = row.getCell(1).value;
                    const rawName = row.getCell(2).value;
                    if (rawId || rawName) {
                        qrcodesToImport.push({
                            id: rawId ? Number(rawId) : null,
                            name: rawName?.toString() || "QR Senza Nome"
                        });
                    }
                }
            });

            if (qrcodesToImport.length === 0) {
                throw new Error("Il file Excel è vuoto o non ha il formato corretto.");
            }

            const payload = {
                idCategory: activeCategory,
                idClient: activeClient,
                note: note,
                validFrom: validFrom,
                validTo: validTo,
                qrCodeAvailabilityDto: availabilities.filter(a => a.from && a.to),
                maxUses: maxUses,
                maxUsesFasce: maxUsesFasce,
                oneShot: isOneShot,
                oneShotName: oneShotName,
                qrcodes: qrcodesToImport
            };

            const response = await api.post("/api/qrcodes/bulk-import", payload);

            if (response.data.status) {
                setImportResults(response.data.data);
            } else {
                throw new Error(response.data.message);
            }
        } catch (err: any) {
            setError(err.message || "Errore durante l'importazione.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownloadZip = async () => {
        if (!importResults) return;
        const newIds = importResults.map(r => r.newId);
        try {
            const response = await api.post("/api/qrcodes/download-zip", { ids: newIds }, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `QRCodes_Importati_${new Date().getTime()}.zip`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (err) {
            alert("Errore durante la generazione dello ZIP");
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-12">
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center text-sky-400">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                </div>
                <div>
                    <h1 className="text-3xl font-extrabold text-white">Importazione Massiva Excel</h1>
                    <p className="text-slate-400 mt-1">Carica un file Excel per generare QR Code in blocco con regole personalizzate.</p>
                </div>
            </div>

            {error && <div className="p-4 bg-red-900/20 border border-red-800/50 text-red-200 rounded-xl">{error}</div>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* --- SEZIONE 1: Dati Principali & File --- */}
                <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 space-y-5">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2 border-b border-slate-700/50 pb-3">Dati Principali</h2>

                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">File Excel (Col A: ID, Col B: Nome) *</label>
                        <input
                            type="file" accept=".xlsx, .xls"
                            onChange={e => setFile(e.target.files ? e.target.files[0] : null)}
                            className="w-full text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-sky-500/10 file:text-sky-400 hover:file:bg-sky-500/20"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Categoria *</label>
                            <select value={activeCategory} onChange={e => setActiveCategory(Number(e.target.value))} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-sky-500 block p-3">
                                <option value={-1}>Seleziona Categoria</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Cliente</label>
                            <select value={activeClient} onChange={e => setActiveClient(Number(e.target.value))} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-sky-500 block p-3">
                                <option value={-1}>Seleziona Cliente</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Note (applicate a tutti)</label>
                        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-sky-500 block p-3" placeholder="Note interne..."/>
                    </div>

                    <label className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-xl cursor-pointer border border-slate-700 hover:border-sky-500/50 transition-colors group">
                        <input type="checkbox" checked={isOneShot} onChange={e => setIsOneShot(e.target.checked)} className="w-5 h-5 rounded border-slate-600 text-sky-500 focus:ring-sky-500 bg-slate-800" />
                        <div>
                            <span className="text-white font-medium group-hover:text-sky-400 transition-colors">Abilita One-Shot</span>
                            <p className="text-xs text-slate-400">Raggruppa i QR code in un blocco monouso combinato.</p>
                        </div>
                    </label>

                    {isOneShot && (
                        <div className="animate-fade-in">
                            <label className="block text-sm font-medium text-slate-300 mb-2">Nome Gruppo One-Shot *</label>
                            <input type="text" value={oneShotName} onChange={e => setOneShotName(e.target.value)} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl block p-3" placeholder="Es. Evento VIP" />
                        </div>
                    )}
                </div>

                {/* --- SEZIONE 2: Regole e Limiti --- */}
                <div className="space-y-6">
                    <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 space-y-5">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2 border-b border-slate-700/50 pb-3">Periodo di Validità</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Data Inizio</label>
                                <input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl block p-3" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Data Fine</label>
                                <input type="date" value={validTo} onChange={e => setValidTo(e.target.value)} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl block p-3" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 space-y-5">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2 border-b border-slate-700/50 pb-3">Limiti di Utilizzo</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Utilizzi Max Totali</label>
                                <input type="number" min="-1" value={maxUses} onChange={e => setMaxUses(Number(e.target.value))} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl block p-3" />
                                <p className="text-xs text-slate-500 mt-1">-1 per illimitati</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Utilizzi Max x Fascia</label>
                                <input type="number" min="-1" value={maxUsesFasce} onChange={e => setMaxUsesFasce(Number(e.target.value))} className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl block p-3" />
                                <p className="text-xs text-slate-500 mt-1">-1 per illimitati</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- SEZIONE 3: Fasce Orarie --- */}
            <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-5 border-b border-slate-700/50 pb-3">
                    <h2 className="text-xl font-bold text-white">Fasce Orarie Giornaliere</h2>
                    <button type="button" onClick={handleAddAvailability} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2">
                        + Aggiungi Fascia
                    </button>
                </div>
                {availabilities.length === 0 && <p className="text-slate-400 italic text-sm text-center py-4">Nessuna fascia oraria impostata. Il QR sarà sempre attivo.</p>}
                <div className="space-y-3">
                    {availabilities.map((avail, index) => (
                        <div key={index} className="flex flex-col sm:flex-row gap-4 items-end bg-slate-900/50 p-4 rounded-xl border border-slate-700 animate-fade-in">
                            <div className="flex-1 w-full">
                                <label className="block text-xs font-medium text-slate-400 mb-1">Dalle ore</label>
                                <input type="time" value={avail.from} onChange={e => handleAvailabilityChange(index, "from", e.target.value)} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg block p-2.5" />
                            </div>
                            <div className="flex-1 w-full">
                                <label className="block text-xs font-medium text-slate-400 mb-1">Alle ore</label>
                                <input type="time" value={avail.to} onChange={e => handleAvailabilityChange(index, "to", e.target.value)} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg block p-2.5" />
                            </div>
                            <button type="button" onClick={() => handleRemoveAvailability(index)} className="p-2.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 rounded-lg transition-colors" title="Rimuovi fascia">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <button
                onClick={handleImport}
                disabled={isProcessing}
                className="w-full py-4 bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white font-bold rounded-xl disabled:opacity-50 shadow-lg shadow-sky-500/20 transition-all text-lg"
            >
                {isProcessing ? "Elaborazione in corso..." : "Avvia Importazione Massiva"}
            </button>

            {/* Modale Risultati */}
            {importResults && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl">
                        <div className="p-6 border-b border-slate-700 bg-slate-800 rounded-t-2xl">
                            <h3 className="text-xl font-bold text-white">Importazione Completata</h3>
                            <p className="text-slate-400 text-sm">Controlla gli ID assegnati e scarica lo ZIP con le immagini.</p>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1 bg-slate-900/50">
                            <ul className="space-y-3">
                                {importResults.map((res, i) => (
                                    <li key={i} className="flex justify-between items-center p-3 bg-slate-800 border border-slate-700 rounded-lg">
                                        <span className="text-white font-medium">{res.name}</span>
                                        <div className="flex items-center gap-2 font-mono text-sm">
                                            {res.idChanged ? (
                                                <>
                                                    <span className="line-through text-red-400" title="ID già occupato">{res.originalId}</span>
                                                    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                                    <span className="text-emerald-400 font-bold">{res.newId}</span>
                                                </>
                                            ) : (
                                                <span className="text-emerald-400 font-bold">{res.newId}</span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="p-6 border-t border-slate-700 bg-slate-800 rounded-b-2xl flex justify-end gap-4">
                            <button onClick={() => setImportResults(null)} className="px-5 py-2.5 text-slate-300 font-medium hover:text-white transition-colors">Chiudi</button>
                            <button onClick={handleDownloadZip} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                                Scarica ZIP Immagini
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BulkImportPage;